import type { JSX } from 'react';

import type { OnboardingTourFieldsFragment } from '../../pages/Onboarding/queries/onboardingTour.fragment.graphql';
import { Button } from '../Button/Button';
import styles from './TourBar.module.css';

export type TourBarAction = 'back' | 'skip' | 'next' | 'pause' | 'resume' | 'finish';

interface TourBarProps {
  tour: OnboardingTourFieldsFragment;
  /** Off while the assistant is answering or a card waits for a confirm. */
  disabled: boolean;
  onAction: (action: TourBarAction) => void;
}

/**
 * Back · Skip · Next and Pause under the docked panel's header, read from the
 * server's tour so it is right after a reload. Paused, it is one "Resume tour"
 * button. On the last step Next becomes Finish, which asks the assistant for
 * the summary.
 */
export function TourBar({ tour, disabled, onAction }: TourBarProps): JSX.Element {
  if (tour.paused) {
    return (
      <div className={styles.Bar} role="group" aria-label="Tour">
        <span className={styles.Note}>Tour paused</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onAction('resume')}
        >
          Resume tour
        </Button>
      </div>
    );
  }
  const last = tour.position >= tour.count;
  return (
    <div className={styles.Bar} role="group" aria-label="Tour">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={styles.Pause}
        disabled={disabled}
        onClick={() => onAction('pause')}
      >
        Pause
      </Button>
      <div className={styles.Moves}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || tour.position <= 1}
          onClick={() => onAction('back')}
        >
          Back
        </Button>
        {!last && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onAction('skip')}
          >
            Skip
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onAction(last ? 'finish' : 'next')}
        >
          {last ? 'Finish tour' : 'Next'}
        </Button>
      </div>
    </div>
  );
}

export default TourBar;
